import { React, useState, useEffect } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CreateUpdateFramework from "../Framework/CreateUpdateFramework";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import EnhancedDataGrid from "../../EnhancedDataGrid/EnhancedDataGrid";
import { Grid, TextField, IconButton, Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Autocomplete from "@mui/material/Autocomplete";
import { useLocation } from "react-router-dom";
import {
  getActiveVisitors,
  getFramworkVisitors,
  updateFramworkVisitors,
  deleteFramworkVisitor,
  addFramworkVisitor,
  getActiveSegments,
  getFramworkSegment,
  addFramworkSegment,
  updateFramworkSegment,
  deleteFramworkSegment,
} from "../../../services/learnPointUserServices";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import classes from "./Classes.module.css";
import LearnpointDefaultMilestones from "components/MileStones/LearnpointDefaultMilestones/LearnpointDefaultMilestones";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import Button from "@mui/material/Button";
import { useAuth } from "context/AuthContext";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={
      <ArrowForwardIosSharpIcon
        sx={{ fontSize: "1.5rem", color: "green", fontWeight: "bold" }}
      />
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, .05)"
      : "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  width: "150px",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "6px 20px",

  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

export default function Classes() {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const update = "update";
  const create = "create";
  const [expanded, setExpanded] = useState("panel1");
  const handleChangePanel = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };
  const currentUser = localStorage.getItem("User");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const [activeVisitor, setActiveVisitor] = useState([]);
  const [visitordataSource, setVisitordataSource] = useState([]);
  const [currentVisitor, setCurrentVisitor] = useState([]);
  const [visitorValues, setVisitorValues] = useState(null);
  const [loading, setLoading] = useState(false);

  const [activeSegment, setActiveSegment] = useState([]);
  const [segmentdataSource, setSegmentdataSource] = useState([]);
  const [currentSegment, setCurrentSegment] = useState([]);

  const newFramework = location.state?.action === create;
  const [frameworkId, setFrameworkId] = useState(0);

  const hasEditAccess = hasPermission("LD_Group_Admin");

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        if (!newFramework) {
          const editFrameworkId = location.state?.frameworkId;

          if (!cancelled) {
            setFrameworkId(editFrameworkId);
          }

          await setupVistors(editFrameworkId);

          if (cancelled) return;

          await setupSegments(editFrameworkId);

          if (cancelled) return;
        }

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "LearnPoint",
            "Classes",
          );
          window.localStorage.removeItem("LPHome");
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [location]);

  useEffect(() => {
    if (hasEditAccess) {
      segmentHeadCells.push({
        fieldName: "isActive",
        label: "Remove",
        control: "remove",
        editable: false,
      });
      visitorHeadCells.push({
        fieldName: "isActive",
        label: "Remove",
        control: "remove",
        editable: false,
      });
    }
  }, []);

  const setupSegments = async (frameworkId) => {
    const activeSegments = await getActiveSegments();
    if (activeSegments.status === true) {
      setActiveSegment(activeSegments.data);
    } else {
      setSnack({
        open: true,
        message: "Failed to get active visitor details",
        severity: "error",
      });
    }
    if (location.state?.action === update) await fetchSegmentData(frameworkId);
  };

  const setupVistors = async (frameworkId) => {
    const activeVisitors = await getActiveVisitors();
    if (activeVisitors.status === true) {
      setActiveVisitor(activeVisitors.data);
    } else {
      setSnack({
        open: true,
        message: "Failed to get active visitor details",
        severity: "error",
      });
    }
    if (location.state?.action === update) await fetchVisitorData(frameworkId);
  };

  //Segment start
  const fetchSegmentData = async (frameworkId) => {
    const framworkSegment = await getFramworkSegment(frameworkId);
    if (framworkSegment.status === true) {
      const dataSegment = framworkSegment.data.map((item) => {
        return {
          id: item.SegmentOrder,
          segmentName: item.Segment,
          duration: item.DurationDays,
          isActive: true,
          FrameworkId: item.FrameworkId,
          FrameworkSegmentId: item.FrameworkSegmentId,
          SegmentId: item.SegmentId,
        };
      });
      setSegmentdataSource(dataSegment);
    } else if (framworkSegment.statusCode === 404) {
      setSnack({
        open: true,
        message: "No segments for this framework",
        severity: "info",
      });
    } else {
      setSnack({
        open: true,
        message: "Internal error occured, failed to load segments.",
        severity: "error",
      });
    }
  };

  const segmentHeadCells = [
    {
      fieldName: "id",
      label: "Order",
      control: "label",
      editable: false,
    },
    {
      fieldName: "segmentName",
      label: "Segment Name",
      control: "label",
      editable: true,
    },
    {
      fieldName: "duration",
      label: "Duration (days)",
      control: "label",
      editable: true,
    },
    {
      fieldName: "isActive",
      label: "Remove",
      control: "remove",
      editable: true,
    },
  ];
  const handleChangeSegment = (value) => {
    setCurrentSegment(value);
  };

  const handleSaveSegment = async (rowId, data) => {
    let update = await updateFramworkSegment(
      data.FrameworkSegmentId,
      data.FrameworkId,
      data.SegmentId,
      data.duration,
      currentUser,
    );
    if (update.message === "Success") {
      setSnack({
        open: true,
        message: "updated successfully.",
        severity: "success",
      });
    }
    await fetchSegmentData(frameworkId);
  };

  const handleAddSegment = async () => {
    let update = await addFramworkSegment(
      frameworkId,
      currentSegment.SegmentId,
      1,
      currentUser,
    );
    if (update.message === "Success") {
      setSnack({
        open: true,
        message: "Added successfully.",
        severity: "success",
      });
    }
    fetchSegmentData(frameworkId);
  };

  const handleRemoveSegment = async (rowId) => {
    let paramData = segmentdataSource.filter((item) => item?.id === rowId);
    let update = await deleteFramworkSegment(paramData[0].FrameworkSegmentId);
    if (update.message === "Success") {
      setSnack({
        open: true,
        message: "deleted successfully.",
        severity: "success",
      });
    }
    fetchSegmentData(frameworkId);
  };
  //Segment end

  //visitor start
  const visitorHeadCells = [
    {
      fieldName: "id",
      label: "Order",
      control: "label",
      editable: false,
    },
    {
      fieldName: "visitorRole",
      label: "Visitor Role",
      control: "label",
      editable: true,
    },
    {
      fieldName: "reqVisit",
      label: "# Of Required Visits",
      control: "label",
      editable: true,
    },
    {
      fieldName: "isActive",
      label: "Remove",
      control: "remove",
      editable: true,
    },
  ];

  const handleSaveVisitor = async (rowId, data) => {
    let update = await updateFramworkVisitors(
      data.FrameworkVisitorId,
      data.FrameworkId,
      data.VisitorId,
      data.reqVisit,
      currentUser,
    );
    if (update.message === "Success") {
      setSnack({
        open: true,
        message: "updated successfully.",
        severity: "success",
      });
    }
    fetchVisitorData(frameworkId);
  };

  const handleRemoveVisitor = async (rowId) => {
    let paramData = visitordataSource.filter((item) => item?.id === rowId);

    let update = await deleteFramworkVisitor(paramData[0].FrameworkVisitorId);
    if (update.message === "Success") {
      setSnack({
        open: true,
        message: "deleted successfully.",
        severity: "success",
      });
    }
    fetchVisitorData(frameworkId);
  };

  const handleAddVisitor = async () => {
    let update = await addFramworkVisitor(
      frameworkId,
      currentVisitor.VisitorId,
      currentUser,
    );
    if (update.message === "Success") {
      setSnack({
        open: true,
        message: "Added successfully.",
        severity: "success",
      });
    }
    setVisitorValues(null);
    fetchVisitorData(frameworkId);
  };

  const handleChangeVisitor = (value) => {
    setCurrentVisitor(value);
  };

  const fetchVisitorData = async (frameworkId) => {
    let data;
    const framworkVisitors = await getFramworkVisitors(frameworkId);
    if (framworkVisitors.status === true) {
      data = framworkVisitors.data.map((item) => {
        return {
          id: item.VisitorOrder,
          visitorRole: item.Visitor,
          reqVisit: item.RequiredVisits,
          isActive: true,
          FrameworkId: item.FrameworkId,
          FrameworkVisitorId: item.FrameworkVisitorId,
          VisitorId: item.VisitorId,
        };
      });

      setVisitordataSource(data);
    } else if (framworkVisitors.statusCode === 404) {
      setSnack({
        open: true,
        message: "No vistors found for this framework.",
        severity: "info",
      });
    } else {
      setSnack({
        open: true,
        message: "Internal error occured, failed to load visitors.",
        severity: "error",
      });
    }
  };
  //visitor end

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <div className={"info-section"}>
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleChangePanel("panel1")}
        >
          <AccordionSummary
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            expandIcon={<ExpandMoreIcon />}
          >
            {newFramework ? <b>Create Framework</b> : <b>Update Framework</b>}
          </AccordionSummary>
          <AccordionDetails>
            <CreateUpdateFramework
              action={newFramework ? "create" : "update"}
              frameworkId={frameworkId}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded === "panel2"}
          onChange={handleChangePanel("panel2")}
        >
          <AccordionSummary
            aria-controls="segment"
            id="segment"
            disabled={newFramework}
            expandIcon={<ExpandMoreIcon />}
          >
            {" "}
            <b>Segment</b>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container rowSpacing={1}>
              <Grid item xs={0.2}></Grid>
              <Grid item xs={1}>
                <p className={classes.fieldText}> Segment :</p>
              </Grid>
              <Grid item xs={3}>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  options={activeSegment}
                  disabled={!hasEditAccess}
                  onChange={(event, value) => handleChangeSegment(value)}
                  getOptionLabel={(option) => option.Segment}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Segment"
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={0.2}></Grid>
              <Grid item xs={0.3}>
                <Tooltip title="Save Segment">
                  <CustomButton
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddSegment}
                  >
                    Add Segment
                  </CustomButton>
                </Tooltip>
              </Grid>
              <Grid item xs={7.4}></Grid>

              <Grid item xs={12}>
                <EnhancedDataGrid
                  key=""
                  headCells={segmentHeadCells}
                  dataSource={segmentdataSource}
                  handleSave={handleSaveSegment}
                  handleRemove={handleRemoveSegment}
                  isReadOnly={!hasEditAccess}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded === "panel4"}
          onChange={handleChangePanel("panel4")}
        >
          <AccordionSummary
            aria-controls="visitor"
            id="visitor"
            disabled={newFramework}
            expandIcon={<ExpandMoreIcon />}
          >
            <b>Required Visitor</b>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container rowSpacing={1}>
              <Grid item xs={0.2}></Grid>
              <Grid item xs={1.2}>
                <p className={classes.fieldText}> Required Visitor :</p>
              </Grid>
              <Grid item xs={3}>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  options={activeVisitor}
                  disabled={!hasEditAccess}
                  onChange={(event, value) => handleChangeVisitor(value)}
                  getOptionLabel={(option) => option.Visitor}
                  value={visitorValues}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Visitor"
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={0.2}></Grid>
              <Grid item xs={0.3}>
                <Tooltip title="Save Visitor">
                  <CustomButton
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddVisitor}
                  >
                    Add Visitor
                  </CustomButton>
                </Tooltip>
              </Grid>
              <Grid item xs={7.2}></Grid>

              <Grid item xs={12}>
                <EnhancedDataGrid
                  key="Visitor"
                  headCells={visitorHeadCells}
                  dataSource={visitordataSource}
                  handleSave={handleSaveVisitor}
                  handleRemove={handleRemoveVisitor}
                  isReadOnly={!hasEditAccess}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded === "panel5"}
          onChange={handleChangePanel("panel5")}
        >
          <AccordionSummary
            aria-controls="Default Milestones"
            id="Default Milestones"
            disabled={newFramework}
            expandIcon={<ExpandMoreIcon />}
          >
            <b>Default Milestones</b>
          </AccordionSummary>
          <AccordionDetails>
            <LearnpointDefaultMilestones
              frameworkId={newFramework ? 0 : location.state?.frameworkId}
            />
          </AccordionDetails>
        </Accordion>
        <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      </div>
    </>
  );
}
